import {CheckCircle2, FileSpreadsheet, FileText, Image as ImageIcon, Loader2, X} from 'lucide-react'
import React, {FC, ReactNode} from 'react'
import {useIntl} from 'react-intl'

import {fileExtension} from '../utils/teacherEntry'

/** One uploaded file and where it stands. */
export interface UploadStatusItem {
  id: string
  file: File
  /** Being uploaded / read: a spinner and `busyText`. */
  busy?: boolean
  busyText?: string
  /** 0..1 while its pieces are sent; null or absent otherwise. */
  progress?: number | null
  /** Why it could not be used; shown in red on the file's line. */
  error?: string | null
  /** A neutral line under the name ("Waiting for your answer"). */
  hint?: string | null
  /** `success`: the hint says what the file held, in green ("5 teachers found"). */
  hintTone?: 'success'
  /** The file's summary, coloured by kind (Bootstrap tones: success, warning, primary…). */
  badges?: Array<{text: string; tone: string}>
  /** Nothing more is needed: green tick and tint, like the Add Teachers files. */
  settled?: boolean
  /** Read together with another line's file: no X of its own (that line's X removes both). */
  locked?: boolean
  /** In the file's box, beside its status: the button that puts its summary into the table. */
  action?: ReactNode
}

/** What the card as a whole is doing: one loader line, as on the Add Teachers card. */
export interface UploadStatusBusy {
  text: string
  /** 0..1 while the files' pieces are sent; null or absent otherwise. */
  progress?: number | null
  hint?: string | null
}

interface Props {
  items: UploadStatusItem[]
  /**
   * Set while the card's files are being uploaded or read: shown once above the lines, and the
   * lines show no spinner of their own (a busy line still can't be removed).
   */
  busy?: UploadStatusBusy | null
  onRemove?: (id: string) => void
  /** Under the lines, inside the box. */
  children?: ReactNode
}

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'webp', 'heic', 'heif'])
const SPREADSHEET_EXTENSIONS = new Set(['xlsx', 'xls', 'csv', 'ods'])

const FileIcon: FC<{file: File}> = ({file}) => {
  const ext = fileExtension(file.name)
  if (SPREADSHEET_EXTENSIONS.has(ext)) return <FileSpreadsheet size={18} aria-hidden />
  if (IMAGE_EXTENSIONS.has(ext)) return <ImageIcon size={18} aria-hidden />
  return <FileText size={18} aria-hidden />
}

const formatSize = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`

/**
 * The Add Teachers file flow under the Students and Teacher Allocation upload buttons: each file
 * shows Uploading, then Reading, then its summary, and nothing reaches the table until the
 * user confirms with the button in its box (`action`). Renders nothing while there is no file.
 */
const UploadStatusPanel: FC<Props> = ({items, busy = null, onRemove, children}) => {
  const intl = useIntl()
  if (items.length === 0) return null
  return (
    <div className='step4-upload-scope'>
      <section className='step4-te-card'>
        {busy && (
          <div className='step4-te-file-body mb-3' role='status' aria-live='polite'>
            <span className='step4-te-file-status step4-te-file-status--busy'>
              <Loader2 size={14} className='step4-te-spin' aria-hidden />
              {busy.text}
            </span>
            {busy.progress != null && (
              <span
                className='step4-te-file-progress'
                role='progressbar'
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(busy.progress * 100)}
              >
                <span style={{width: `${Math.round(busy.progress * 100)}%`}} />
              </span>
            )}
            {busy.hint && <span className='step4-te-file-hint'>{busy.hint}</span>}
          </div>
        )}
        <ul className='step4-te-files mb-0' aria-live='polite'>
          {items.map((item) => (
            <li
              key={item.id}
              className='step4-te-file'
              style={
                item.settled
                  ? {
                      background: 'rgba(80, 205, 137, 0.08)',
                      borderColor: 'rgba(80, 205, 137, 0.45)',
                    }
                  : undefined
              }
            >
              <span className='step4-te-file-icon'>
                <FileIcon file={item.file} />
              </span>
              <span className='step4-te-file-body'>
                <span className='d-flex align-items-center gap-2' style={{minWidth: 0}}>
                  <span className='step4-te-file-name' title={item.file.name}>
                    {item.file.name}
                  </span>
                  {item.settled && (
                    <CheckCircle2
                      size={16}
                      style={{color: 'var(--s4te-success)', flex: '0 0 auto'}}
                      aria-label={intl.formatMessage({
                        id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_SETTLED',
                      })}
                    />
                  )}
                </span>
                <span className='step4-te-file-meta'>
                  <span className='step4-te-file-size'>{formatSize(item.file.size)}</span>
                  {item.busy && !busy && (
                    <>
                      {' · '}
                      <span className='step4-te-file-status step4-te-file-status--busy'>
                        <Loader2 size={14} className='step4-te-spin' aria-hidden />
                        {item.busyText}
                      </span>
                    </>
                  )}
                  {item.error && (
                    <>
                      {' · '}
                      <span className='step4-te-file-status step4-te-file-status--error'>
                        {item.error}
                      </span>
                    </>
                  )}
                </span>
                {item.busy && !busy && item.progress != null && (
                  <span
                    className='step4-te-file-progress'
                    role='progressbar'
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(item.progress * 100)}
                  >
                    <span style={{width: `${Math.round(item.progress * 100)}%`}} />
                  </span>
                )}
                {item.hint && (
                  <span
                    className={`step4-te-file-hint${item.hintTone === 'success' ? ' text-success fw-semibold' : ''}`}
                  >
                    {item.hint}
                  </span>
                )}
                {item.badges && item.badges.length > 0 && (
                  <span className='d-flex flex-wrap gap-2 mt-2'>
                    {item.badges.map((badge) => (
                      <span
                        key={badge.text}
                        className={`badge badge-light-${badge.tone} fw-semibold`}
                        style={{fontSize: '0.74rem', padding: '0.35rem 0.6rem'}}
                      >
                        {badge.text}
                      </span>
                    ))}
                  </span>
                )}
              </span>
              {item.action && (
                <span className='d-flex align-items-center' style={{flex: '0 0 auto'}}>
                  {item.action}
                </span>
              )}
              {onRemove && !item.busy && !item.locked && (
                <button
                  type='button'
                  className='step4-te-file-remove'
                  onClick={() => onRemove(item.id)}
                  aria-label={intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.REMOVE_FILE',
                  })}
                  title={intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.REMOVE_FILE',
                  })}
                >
                  <X size={16} aria-hidden />
                </button>
              )}
            </li>
          ))}
        </ul>
        {children}
      </section>
    </div>
  )
}

export default UploadStatusPanel
